import Phaser from 'phaser';
import { Behavior, BehaviorConfig } from './Behavior';
import { Entity } from '../base/Entity';

export interface FlyerConfig extends BehaviorConfig {
  speed?: number;
  hoverAmplitude?: number;
  hoverSpeed?: number;
}

/**
 * FlyerBehavior - Flying/hovering movement for entities
 *
 * Used by: Bat, Helicopter, JetpackHuman, etc.
 *
 * Features:
 * - No gravity (floats)
 * - Hover effect (oscillation)
 * - Fly to position
 * - Chase target in air
 *
 * REQUIRED CONFIG (must come from JSON, no defaults):
 * - speed: number
 * - hoverAmplitude: number
 * - hoverSpeed: number
 */
export class FlyerBehavior extends Behavior {
  public speed: number;
  public hoverAmplitude: number;
  public hoverSpeed: number;
  public baseY: number;
  public isHovering: boolean = true;
  public isFlying: boolean = false;
  public flyTarget?: { x: number; y: number };

  private hoverTime: number = 0;
  private flyTween?: Phaser.Tweens.Tween;

  constructor(entity: Entity, config: FlyerConfig = {}) {
    super(entity, config);

    const entityName = entity.texture?.key ?? entity.entityType ?? 'unknown';

    // Validate required config - no fallbacks
    const missing: string[] = [];
    if (config.speed === undefined) missing.push('speed');
    if (config.hoverAmplitude === undefined) missing.push('hoverAmplitude');
    if (config.hoverSpeed === undefined) missing.push('hoverSpeed');

    if (missing.length > 0) {
      console.error(`[FlyerBehavior] Missing required config for entity "${entityName}":`, missing);
      console.error('[FlyerBehavior] Stack trace:', new Error().stack);
      this.enabled = false;
      // Assign dummy values only to avoid undefined (but the behavior is disabled)
      this.speed = 0;
      this.hoverAmplitude = 0;
      this.hoverSpeed = 0;
      this.baseY = entity.y;
      return;
    }

    this.speed = config.speed;
    this.hoverAmplitude = config.hoverAmplitude;
    this.hoverSpeed = config.hoverSpeed;
    this.baseY = entity.y;

    // Initialize entity.state.behaviors.flyer for AbductableBehavior to check
    this.entity.state.behaviors.flyer = { isFlying: false };

    // Flyers don't have gravity (PlatformBehavior will manage it when not flying)
    if (entity.body) {
      (entity.body as Phaser.Physics.Arcade.Body).setAllowGravity(false);
    }

    // Listen for entityLanded to reset baseY after falling
    this.scene.events.on('entityLanded', this.onEntityLanded, this);
  }

  /**
   * When entity lands (post-abduction), evaluate if we should resume flying
   * Subclasses can override to add logic (e.g., only fly when player is near)
   */
  private onEntityLanded(data: { entity: Entity }): void {
    if (data.entity !== this.entity) return;

    // By default, resume hovering when landing
    // Subclasses or AI controllers can call startFlying() when appropriate
    this.baseY = this.entity.y;
  }

  /**
   * Update hover effect
   *
   * State-based communication with AbductableBehavior:
   * - If being abducted or falling post-release, don't hover
   */
  update(time: number, delta: number): void {
    if (!this.enabled) return;

    // Check abduction state via entity.state.behaviors (namespaced communication)
    const abductionState = this.entity.state.behaviors.abductable?.state;
    if (abductionState === 'lifting' || abductionState === 'released') {
      // Being abducted or falling - stop hovering
      this.isHovering = false;
      this.entity.state.behaviors.flyer!.isFlying = false;
      return;
    }

    if (this.isHovering && !this.isFlying) {
      this.updateHover(delta);
    }
  }

  /**
   * Update hover oscillation
   */
  private updateHover(delta: number): void {
    this.hoverTime += delta / 1000;
    const hoverOffset = Math.sin(this.hoverTime * this.hoverSpeed) * this.hoverAmplitude;
    this.entity.y = this.baseY + hoverOffset;
  }

  /**
   * Start flying - enables hover and sets isFlying state
   */
  startFlying(): void {
    this.isHovering = true;
    this.isFlying = false; // Not in flyTo tween
    this.entity.state.behaviors.flyer!.isFlying = true;
    this.baseY = this.entity.y;
    this.hoverTime = 0;

    // Disable gravity while flying (FlyerBehavior controls Y)
    if (this.entity.body) {
      (this.entity.body as Phaser.Physics.Arcade.Body).setAllowGravity(false);
    }
  }

  /**
   * Stop flying - disables hover and clears isFlying state
   * PlatformBehavior will handle gravity/falling
   */
  stopFlying(): void {
    // Cancel any flyTo tween
    if (this.flyTween) {
      this.flyTween.stop();
      this.flyTween = undefined;
    }

    this.isHovering = false;
    this.isFlying = false;
    this.entity.state.behaviors.flyer!.isFlying = false;
    this.flyTarget = undefined;

    // Stop velocity
    if (this.entity.body) {
      (this.entity.body as Phaser.Physics.Arcade.Body).setVelocity(0, 0);
    }
  }

  /**
   * Fly to a specific position
   */
  flyTo(x: number, y: number, duration: number = 1000, onComplete?: () => void): void {
    // Cancel existing fly
    if (this.flyTween) {
      this.flyTween.stop();
    }

    this.isFlying = true;
    this.entity.state.behaviors.flyer!.isFlying = true;
    this.flyTarget = { x, y };

    this.flyTween = this.scene.tweens.add({
      targets: this.entity,
      x: x,
      y: y,
      duration: duration,
      ease: 'Sine.easeInOut',
      onComplete: () => {
        this.isFlying = false;
        // Keep state.behaviors.flyer.isFlying = true since we're still hovering
        this.baseY = y;
        this.flyTarget = undefined;
        onComplete?.();
      }
    });
  }

  /**
   * Fly toward a target entity
   */
  flyToward(target: Entity, speed?: number): void {
    if (!this.entity.body) return;

    const flySpeed = speed ?? this.speed;
    const angle = Phaser.Math.Angle.Between(
      this.entity.x, this.entity.y,
      target.x, target.y
    );

    const body = this.entity.body as Phaser.Physics.Arcade.Body;
    body.setVelocity(
      Math.cos(angle) * flySpeed,
      Math.sin(angle) * flySpeed
    );
  }

  /**
   * Stop flying and hover in place
   */
  stopAndHover(): void {
    if (this.flyTween) {
      this.flyTween.stop();
      this.flyTween = undefined;
    }

    if (this.entity.body) {
      (this.entity.body as Phaser.Physics.Arcade.Body).setVelocity(0, 0);
    }

    this.isFlying = false;
    this.baseY = this.entity.y;
    this.isHovering = true;
    // Keep isFlying = true since we're still hovering (just not in a tween)
    this.entity.state.behaviors.flyer!.isFlying = true;
  }

  /**
   * Enable/disable hover
   */
  setHovering(enabled: boolean): void {
    this.isHovering = enabled;
    if (enabled) {
      this.baseY = this.entity.y;
      this.hoverTime = 0;
    }
  }

  /**
   * Set new base Y for hovering
   */
  setBaseY(y: number): void {
    this.baseY = y;
  }

  /**
   * Move in a direction
   */
  move(dirX: number, dirY: number, speed?: number): void {
    if (!this.entity.body) return;

    const moveSpeed = speed ?? this.speed;
    const body = this.entity.body as Phaser.Physics.Arcade.Body;

    // Normalize direction
    const length = Math.sqrt(dirX * dirX + dirY * dirY);
    if (length > 0) {
      body.setVelocity(
        (dirX / length) * moveSpeed,
        (dirY / length) * moveSpeed
      );
    }
  }

  /**
   * Stop all movement (but keep hovering state)
   */
  stop(): void {
    if (this.flyTween) {
      this.flyTween.stop();
      this.flyTween = undefined;
    }

    if (this.entity.body) {
      (this.entity.body as Phaser.Physics.Arcade.Body).setVelocity(0, 0);
    }

    this.isFlying = false;
    // Note: Does NOT set state.behaviors.flyer.isFlying = false, use stopFlying() for that
  }

  /**
   * Get distance to target
   */
  distanceTo(x: number, y: number): number {
    return Phaser.Math.Distance.Between(this.entity.x, this.entity.y, x, y);
  }

  /**
   * Clean up
   */
  destroy(): void {
    if (this.flyTween) {
      this.flyTween.stop();
      this.flyTween = undefined;
    }

    // Clean up state by removing namespace
    delete this.entity.state.behaviors.flyer;

    // Remove event listener
    this.scene.events.off('entityLanded', this.onEntityLanded, this);

    super.destroy();
  }
}
